import { useState, useEffect, useCallback } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, Loader2, Infinity } from 'lucide-react';

const TIER_COLORS = {
  economy: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  standard: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  priority: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const TIER_ORDER = ['economy', 'standard', 'priority'];

const FIELDS = [
  { key: 'storage_limit_gb', label: 'Storage (GB)', gbField: true, placeholder: 'e.g., 5' },
  { key: 'url_count_limit', label: 'URL Count', gbField: false, placeholder: 'e.g., 100' },
  { key: 'concurrent_jobs_limit', label: 'Concurrent Jobs', gbField: false, placeholder: 'e.g., 10' },
  { key: 'daily_requests_limit', label: 'Daily Requests', gbField: false, placeholder: 'e.g., 1000' },
];

export default function TierLimits() {
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getTierLimits();
      setTiers(res.data);
    } catch (err) { toast.error('Failed to load tier limits'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (tier, field, value) => {
    setTiers(prev => ({
      ...prev,
      [tier]: { ...prev[tier], [field]: value }
    }));
  };

  const toggleUnlimited = (tier, field) => {
    const current = tiers[tier]?.[field];
    if (current === null) {
      handleChange(tier, field, field === 'storage_limit_gb' ? 5 : 100);
    } else {
      handleChange(tier, field, null);
    }
  };

  const handleSave = async (tier) => {
    setSaving(s => ({ ...s, [tier]: true }));
    try {
      const t = tiers[tier];
      const payload = {};

      for (const f of FIELDS) {
        const val = t[f.key];
        if (val === null || val === undefined || val === '') {
          payload[f.key] = null;
        } else {
          const num = Number(val);
          if (isNaN(num) || num < 0) {
            toast.error(`${f.label}: must be a non-negative number or Unlimited`);
            setSaving(s => ({ ...s, [tier]: false }));
            return;
          }
          payload[f.key] = num;
        }
      }

      // Backend expects storage_limit_gb (converts to bytes)
      await adminApi.updateTierLimit(tier, payload);
      toast.success(`${tier} limits saved`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(s => ({ ...s, [tier]: false }));
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div data-testid="tier-limits-page" className="space-y-4">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Tier Limits</h2>
        <p className="text-muted-foreground mt-1 text-sm">Configure per-tier resource limits. Unlimited (toggle on) means no enforcement for that field.</p>
      </div>

      {TIER_ORDER.map(tier => {
        const t = tiers[tier] || {};
        return (
          <Card key={tier} className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base capitalize">{tier}</CardTitle>
                  <Badge variant="outline" className={`text-[10px] ${TIER_COLORS[tier]}`}>{tier}</Badge>
                </div>
                <Button data-testid={`save-${tier}`} size="sm" onClick={() => handleSave(tier)} disabled={saving[tier]} className="bg-primary hover:bg-primary/90">
                  {saving[tier] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {FIELDS.map(f => {
                  const val = t[f.key];
                  const isUnlimited = val === null || val === undefined;
                  return (
                    <div key={f.key} className="space-y-2">
                      <Label className="text-xs">{f.label}</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid={`${tier}-${f.key}`}
                          type="number"
                          min="0"
                          step={f.gbField ? '0.1' : '1'}
                          className="h-8 text-xs font-mono flex-1"
                          value={isUnlimited ? '' : val}
                          onChange={e => handleChange(tier, f.key, e.target.value === '' ? null : Number(e.target.value))}
                          disabled={isUnlimited}
                          placeholder={isUnlimited ? 'Unlimited' : f.placeholder}
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Switch
                          data-testid={`${tier}-${f.key}-unlimited`}
                          checked={isUnlimited}
                          onCheckedChange={() => toggleUnlimited(tier, f.key)}
                        />
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          {isUnlimited ? <><Infinity className="w-3 h-3" /> Unlimited</> : 'Limited'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
